import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseDallas } from "@/components/site/WhyChooseDallas";
import kurtWagner from "@/assets/restore/kurt-wagner.webp.asset.json";
import drHarrisScott from "@/assets/restore/dr-harris-scott.webp.asset.json";
import { doctors } from "@/lib/site-data";
import { DALLAS_META, DALLAS_FAQ, DALLAS_REVIEWS, DALLAS_REVIEWS_DESCRIPTION, DALLAS_COPY, DALLAS_GUARANTEE, DALLAS_PRICING, DALLAS_SURGEONS } from "@/lib/wp-restore/cities";
import { safeJsonLd } from "@/lib/structured-data";
import { getDallasStructuredData } from "@/lib/structured-data/dallas";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapDallas = lazy(() => import("@/components/site/LocationsMapDallas").then(m => ({ default: m.LocationsMapDallas })));
const VirtualConsult = lazy(() => import("@/components/site/VirtualConsult").then(m => ({ default: m.VirtualConsult })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

const FUE_VIDEO_URL = "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/F.U.E.ExplainedRESTOREHairWhatisFUE.mp4";

export const Route = createFileRoute("/locations/restore-dallas")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-dallas/" }],
    meta: [
      { title: DALLAS_META.title },
      { name: "description", content: DALLAS_META.description },
      { property: "og:title", content: "Get Your Hair Back | RESTORE® Hair Transplants — Dallas" },
      {
        property: "og:description",
        content:
          "Top hair restoration team trusted by celebrities, athletes & everyday guys. Soft, natural, undetectable results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getDallasStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroChicago
          eyebrow="RESTORE® | Dallas Hair Transplant"
          eyebrowTag="h1"
          headingTag="h2"
          subheadingTag="h2"
          subheading="RESTORE Your Confidence with the top hair restoration team in Dallas."
        />
        <NorwoodQuiz />
        <WhyChooseDallas />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results showDisclaimer nameTag="h2" />
          </div>
          <ValueProps
            wpCopy={DALLAS_COPY}
            hideEyebrowMobile
            imageSrc={drHarrisScott.url}
            imageAlt="Dr. James Harris with RESTORE client Scott N."
            videoUrl={FUE_VIDEO_URL}
          />
          <NoShaveFuture wpCopy={DALLAS_COPY.future} />
          <NoShaveWhy dougLeft wpCopy={DALLAS_COPY.why} />
          <Reviews ssrMore items={DALLAS_REVIEWS} eyebrow="Testimonials from real RESTORE clients who had a hair transplant" description={DALLAS_REVIEWS_DESCRIPTION} />
          <Guarantee wpCopy={DALLAS_GUARANTEE} />
          <Pricing
            wpCopy={DALLAS_PRICING}
            imageSrc={kurtWagner.url}
            imageAlt="Kurt Wagner, RESTORE Hair Client"
            city="Dallas"
            hidePlayOverlay
          />
          <DoctorBio />
          <Surgeons eyebrowTag="h3" eyebrow={DALLAS_SURGEONS.eyebrow} heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid nameTag="h2" doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Fahad Juboori"].includes(d.name))} />
          <LocationsMapDallas />
          <Faq items={DALLAS_FAQ} numbered headingTag="h3" questionTag="h4" />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}